/**
 * Shell-owned VHS overlay — one instance for the whole SPA.
 */
export function startVhs({ intro = false } = {}) {
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  function mount(className) {
    const el = document.createElement('div');
    el.className = className;
    document.body.appendChild(el);
    return el;
  }

  mount('nt-vhs-patina');
  mount('nt-vhs-interlace');
  mount('nt-vhs-bleed');
  const headSwitch = mount('nt-vhs-headswitch');

  const glitchCanvas = document.createElement('canvas');
  glitchCanvas.className = 'nt-vhs-glitch';
  document.body.appendChild(glitchCanvas);

  const noiseCanvas = document.createElement('canvas');
  noiseCanvas.className = 'nt-vhs-noise';
  document.body.appendChild(noiseCanvas);

  const gctx = glitchCanvas.getContext('2d');
  const nctx = noiseCanvas.getContext('2d');

  let glitchUntil = 0;
  let nextGlitchAt = performance.now() + rand(9000, 24000);
  let glitchBandY = 0.45;
  let glitchShift = 0;
  const dropouts = [];
  const NOISE_POOL_SIZE = 8;
  let noiseBaked = [];
  let noisePoolIdx = 0;
  let trackingY = 0.2;
  let noiseFrame = 0;
  const noiseSkip = intro ? 2 : 3;
  let paused = false;
  let headFlickerTimer = null;
  let rafId = null;

  function rand(a, b) {
    return a + Math.random() * (b - a);
  }

  function bakeNoisePool() {
    const w = noiseCanvas.width;
    const h = noiseCanvas.height;
    if (!w || !h) return;
    noiseBaked = [];
    for (let f = 0; f < NOISE_POOL_SIZE; f++) {
      const img = nctx.createImageData(w, h);
      const d = img.data;
      for (let i = 0; i < d.length; i += 4) {
        const v = (Math.random() * 255) | 0;
        d[i] = d[i + 1] = d[i + 2] = v;
        d[i + 3] = 255;
      }
      noiseBaked.push(img);
    }
    noisePoolIdx = 0;
  }

  function resize() {
    glitchCanvas.width = Math.ceil(window.innerWidth / 3);
    glitchCanvas.height = Math.ceil(window.innerHeight / 3);
    noiseCanvas.width = Math.ceil(window.innerWidth / 4);
    noiseCanvas.height = Math.ceil(window.innerHeight / 4);
    bakeNoisePool();
  }
  resize();
  window.addEventListener('resize', resize);

  function maybeStartGlitch(now) {
    if (now < glitchUntil || now < nextGlitchAt) return;
    glitchUntil = now + rand(280, 1100);
    glitchBandY = rand(0.08, 0.82);
    glitchShift = (Math.random() < 0.5 ? -1 : 1) * rand(3, 16);
    nextGlitchAt = now + rand(11000, 28000);
  }

  function spawnDropout() {
    if (Math.random() > 0.018) return;
    dropouts.push({
      y: Math.random(),
      h: rand(0.0015, 0.01),
      life: 1,
      shift: rand(-6, 6),
    });
    if (dropouts.length > 6) dropouts.shift();
  }

  function drawNoiseStrip(ctx, w, h, alpha) {
    const img = ctx.createImageData(w, h);
    const d = img.data;
    for (let i = 0; i < d.length; i += 4) {
      const v = (Math.random() * 255) | 0;
      d[i] = d[i + 1] = d[i + 2] = v;
      d[i + 3] = alpha;
    }
    return img;
  }

  function drawGlitch(now) {
    const w = glitchCanvas.width;
    const h = glitchCanvas.height;
    if (!w || !h) return;

    gctx.clearRect(0, 0, w, h);
    const active = now < glitchUntil;
    glitchCanvas.style.opacity = active
      ? String(rand(0.22, 0.48))
      : String(dropouts.length ? rand(0.08, 0.14) : 0.07);

    trackingY += 0.00006;
    if (trackingY > 1.04) trackingY = 0;
    gctx.fillStyle = 'rgba(255, 255, 255, 0.05)';
    gctx.fillRect(0, Math.floor(trackingY * h), w, 1);

    spawnDropout();
    for (let i = dropouts.length - 1; i >= 0; i--) {
      const d = dropouts[i];
      d.life -= 0.035;
      if (d.life <= 0) {
        dropouts.splice(i, 1);
        continue;
      }
      const dy = Math.floor(d.y * h);
      const dh = Math.max(1, Math.floor(d.h * h));
      gctx.putImageData(drawNoiseStrip(gctx, w, dh, Math.floor(d.life * 140)), d.shift, dy);
    }

    if (!active) return;
    const bandTop = Math.floor(glitchBandY * h);
    const bandH = Math.max(3, Math.floor(h * rand(0.035, 0.11)));
    const alpha = 150 + ((Math.random() * 80) | 0);
    gctx.putImageData(drawNoiseStrip(gctx, w, bandH, alpha), glitchShift, bandTop);
    gctx.globalCompositeOperation = 'screen';
    gctx.fillStyle = 'rgba(255, 48, 32, 0.14)';
    gctx.fillRect(glitchShift + 2, bandTop, w, bandH);
    gctx.fillStyle = 'rgba(48, 220, 96, 0.10)';
    gctx.fillRect(glitchShift - 1, bandTop, w, bandH);
    gctx.fillStyle = 'rgba(48, 96, 255, 0.10)';
    gctx.fillRect(glitchShift - 3, bandTop, w, bandH);
    gctx.globalCompositeOperation = 'source-over';
  }

  function loop(now) {
    if (paused) return;
    // Intentionally do not transform documentElement — that creates a containing
    // block for position:fixed and can push START / chrome off-screen.
    maybeStartGlitch(now);
    if (!reducedMotion) drawGlitch(now);
    if (++noiseFrame % noiseSkip === 0 && noiseBaked.length) {
      nctx.putImageData(noiseBaked[noisePoolIdx], 0, 0);
      noisePoolIdx = (noisePoolIdx + 1) % noiseBaked.length;
    }
    rafId = requestAnimationFrame(loop);
  }

  function headFlicker() {
    headSwitch.style.opacity = String(0.5 + Math.random() * 0.4);
    headFlickerTimer = setTimeout(headFlicker, 70 + Math.random() * 130);
  }

  document.addEventListener('visibilitychange', () => {
    paused = document.hidden;
    if (!paused) {
      rafId = requestAnimationFrame(loop);
      if (!reducedMotion && !intro) headFlicker();
    } else {
      clearTimeout(headFlickerTimer);
    }
  });

  if (!reducedMotion && !intro) headFlicker();
  rafId = requestAnimationFrame(loop);

  return {
    stop() {
      paused = true;
      if (rafId) cancelAnimationFrame(rafId);
      clearTimeout(headFlickerTimer);
    },
  };
}
